import { supabase, extractEdgeFunctionError } from './client';
import ErrorHandler from '@/utils/ErrorHandler';
import { isTransientError } from '@/utils/requestQueue';
import Toast from 'react-native-toast-message';

/**
 * Client for the `auto-schedule` edge function (AI volunteer scheduling).
 *
 * Contract: ai-volunteer-auto-scheduling-implementation.md §8.1. All actions
 * are POSTs to a single edge function. Errors use the machine envelope
 * `{ error: { code, message, details, retryable } }`; this module normalizes
 * every failure — edge HTTP error, gateway/relay error, fetch failure, or a
 * 2xx body carrying the envelope — into an AutoScheduleError so callers can
 * branch on `error.code` instead of parsing transport errors.
 *
 * No screen/component should call the edge function directly, and nothing in
 * this module writes TaskInstancePeople, tasks, or notifications: assignment
 * writes only happen server-side inside `confirm`.
 */

/** Confirmed scheduling horizons (spec §1.1). UI labels, API validation, and tests all use these. */
export const AUTO_SCHEDULE_HORIZONS = [7, 30, 60];
export const AUTO_SCHEDULE_DEFAULT_HORIZON = 30;
export const AUTO_SCHEDULE_GENERATION_TIMEOUT_MS = 15 * 60 * 1000;
/**
 * Client-side bound on the `generate` invoke itself. A suspended screen can
 * stall the fetch for minutes; bounding it lets the modal retry quickly with
 * the SAME clientRequestID (server replays the run idempotently).
 */
export const AUTO_SCHEDULE_GENERATE_REQUEST_TIMEOUT_MS = 45 * 1000;

/** Run statuses returned by the edge function (spec §7.1). */
export const AUTO_SCHEDULE_ACTIVE_STATUSES = ['queued', 'generating'];
export const AUTO_SCHEDULE_TERMINAL_STATUSES = ['ready', 'confirmed', 'failed', 'canceled', 'expired'];

/** Machine error codes that mark the stored draft no longer reviewable (spec §8.1 table). */
export const AUTO_SCHEDULE_STALE_CODES = ['stale_preview', 'recommendations_changed'];
export const AUTO_SCHEDULE_EXPIRED_CODES = ['preview_expired'];
/** Model/generation failure codes — the draft was never viable (spec §8.1). */
export const AUTO_SCHEDULE_MODEL_FAILURE_CODES = ['model_unavailable', 'invalid_model_response', 'scheduler_not_configured'];

/**
 * Error carrying the edge contract's machine code for UI-level handling.
 * `expected` is true whenever the server returned a known contract envelope —
 * callers should render the mapped state and skip Sentry reporting.
 */
export class AutoScheduleError extends Error {
    constructor(code, message, { details = null, retryable = false, httpStatus = null, originalError = null } = {}) {
        super(message || 'Something went wrong');
        this.name = 'AutoScheduleError';
        this.code = code ?? null;
        this.details = details ?? null;
        this.retryable = !!retryable;
        this.httpStatus = httpStatus ?? null;
        this.originalError = originalError ?? null;
        this.expected = !!code;
    }
}

/**
 * Normalize a supabase.functions.invoke result into an AutoScheduleError, or
 * return null when there is no error.
 */
const normalizeEdgeError = async (error, data) => {
    const extracted = await extractEdgeFunctionError(error, data);
    if (!extracted) return null;

    // The contract envelope is { error: { code, message, details, retryable } }.
    // extractEdgeFunctionError already parsed the non-2xx body into
    // `extracted.data` (or the 2xx {error} body arrives as `data`).
    const envelope = extracted.data?.error;
    const hasEnvelope = envelope && typeof envelope === 'object' && !Array.isArray(envelope);
    const code = hasEnvelope && typeof envelope.code === 'string' ? envelope.code : null;
    const details = hasEnvelope ? envelope.details ?? null : null;
    const message = (hasEnvelope && typeof envelope.message === 'string' && envelope.message)
        || (typeof envelope === 'string' ? envelope : null)
        || (typeof extracted.message === 'string' ? extracted.message : 'Something went wrong');
    const retryable = hasEnvelope && typeof envelope.retryable === 'boolean'
        ? envelope.retryable
        : isTransientError(error) || /fetch|network|timeout|timed out|load failed|relay|abort|failed to send/i.test(String(extracted.message || ''));

    return new AutoScheduleError(code, message, {
        details,
        retryable,
        httpStatus: extracted.httpStatus ?? null,
        originalError: extracted.error ?? null,
    });
};

const invokeAutoSchedule = async (body, { operation, toastOnContractError = false, timeoutMs = null } = {}) => {
    let result;
    try {
        // functions-js supports a native `timeout` (ms → AbortSignal) since
        // v2.49 — pass it through only where the caller opts in.
        const invokeOptions = { body };
        if (Number.isFinite(timeoutMs) && timeoutMs > 0) invokeOptions.timeout = timeoutMs;
        result = await supabase.functions.invoke('auto-schedule', invokeOptions);
    } catch (caught) {
        // invoke() itself only rejects on unexpected client failures.
        if (!isTransientError(caught)) {
            ErrorHandler(caught instanceof Error ? caught : Error(caught?.message || 'Auto-Schedule request failed'), { query: 'auto-schedule', action: body?.action }, 'Something went wrong contacting the scheduler');
        }
        throw new AutoScheduleError(null, caught?.message || 'Something went wrong contacting the scheduler', { retryable: isTransientError(caught), originalError: caught });
    }

    const normalized = await normalizeEdgeError(result.error, result.data);
    if (normalized) {
        // Contract-coded failures are expected UX states (stale preview,
        // permission, expiry…) — throw quietly so the modal can render them.
        // Uncoded, non-transient failures go through Sentry per app convention.
        if (!normalized.expected && !normalized.retryable) {
            ErrorHandler(normalized, { query: 'auto-schedule', action: body?.action, operation, httpStatus: normalized.httpStatus }, 'Something went wrong with Auto-Schedule');
        }
        if (toastOnContractError && normalized.expected) {
            Toast.show({ type: 'error', text2: normalized.message });
        }
        throw normalized;
    }
    return result.data;
};

/**
 * Start a generation run. `clientRequestID` is the caller's idempotency key —
 * generate it once per explicit request, never per render.
 * @returns Promise<{runID, status, scope, window, progress, pollAfterMs}>
 */
export const generateAutoSchedule = ({ clientRequestID, teamIDs, eventID = null, horizonDays = AUTO_SCHEDULE_DEFAULT_HORIZON }) =>
    invokeAutoSchedule({
        action: 'generate',
        clientRequestID,
        teamIDs,
        eventID,
        horizonDays,
    }, { operation: 'generate', timeoutMs: AUTO_SCHEDULE_GENERATE_REQUEST_TIMEOUT_MS });

/**
 * Poll a run. Returns progress ({status, progress, pollAfterMs, …}) while
 * queued/generating, or the hydrated ready preview once `status === 'ready'`.
 */
export const getAutoScheduleStatus = ({ runID }) =>
    invokeAutoSchedule({ action: 'status', runID }, { operation: 'status' });

/**
 * Validate the exact edited draft. `assignments` must include EVERY preview
 * target row (empty personIDs allowed); `acknowledgedWarnings` is the exact
 * set of per-target/person/code acknowledgments the user made.
 * @returns Promise<{validationToken, draftHash, previewRevision, validationExpiresAt, summary?}>
 */
export const validateAutoSchedule = ({ runID, previewRevision, assignments, acknowledgedWarnings = [] }) =>
    invokeAutoSchedule({
        action: 'validate',
        runID,
        previewRevision,
        assignments,
        acknowledgedWarnings,
    }, { operation: 'validate' });

/**
 * Confirm a previously validated draft. `idempotencyKey` must be the SAME key
 * for every retry of this confirmation — generate once per modal session.
 * @returns Promise<receipt>
 */
export const confirmAutoSchedule = ({ runID, validationToken, idempotencyKey }) =>
    invokeAutoSchedule({
        action: 'confirm',
        runID,
        validationToken,
        idempotencyKey,
    }, { operation: 'confirm' });

/** Cancel a queued/generating/ready run. Never undoes confirmed assignments. */
export const cancelAutoSchedule = ({ runID }) =>
    invokeAutoSchedule({ action: 'cancel', runID }, { operation: 'cancel' });

// ── "Do not suggest" exclusions (AutoScheduleExclusions) ────────────────────
// Direct supabase.rpc calls (authenticated) — NOT the edge function. The SQL
// raises machine codes as the exception message; PostgREST wraps them, so the
// leading snake_case token of error.message is the contract code.
const EXCLUSION_ERROR_COPY = {
    not_team_assigner: 'You need assigner access on this team to change suggestions.',
    invalid_scope: 'That task or event is no longer available.',
    invalid_request: 'That person is no longer available.',
    exclusion_not_found: 'That rule was already removed.',
    schedule_busy: 'The scheduler is busy — try again in a moment.',
    authentication_required: 'Sign in again to change suggestions.',
    membership_required: 'Your Reach membership is no longer active.',
    ai_permission_required: 'Auto-Schedule needs the “Use AI Assistant” permission.',
    feature_unavailable: 'Auto-Schedule is not available on this plan.',
};

const throwExclusionError = (error, operation) => {
    const code = /^[a-z][a-z0-9_]{2,60}/.exec(error?.message || '')?.[0] ?? null;
    const friendly = code ? EXCLUSION_ERROR_COPY[code] : null;
    // Expected contract codes are user-facing states — skip Sentry like the
    // edge path does. Uncoded/non-transient failures report normally.
    if (!friendly && !isTransientError(error)) {
        ErrorHandler(error instanceof Error ? error : Error(error?.message || 'Exclusion update failed'), { query: 'auto-schedule-exclusions', operation }, 'Something went wrong updating suggestions');
    }
    const wrapped = new AutoScheduleError(code, friendly || 'Something went wrong updating suggestions', { originalError: error });
    Toast.show({ type: 'error', text2: wrapped.message });
    throw wrapped;
};

/**
 * Read the org's "do not suggest" rules for a set of tasks (RLS already
 * scopes rows to the caller's org). Cheap per-preview lookup used to drive
 * the add/remove toggle state on suggestion rows.
 * @returns Promise<Array<{id, personID, taskID, eventID}>>
 */
export const fetchAutoScheduleExclusions = async ({ taskIDs = [] }) => {
    if (!taskIDs.length) return [];
    const { data, error } = await supabase
        .from('AutoScheduleExclusions')
        .select('id, personID, taskID, eventID')
        .in('taskID', taskIDs);
    if (error) {
        // Read failure must never break the preview — the menu just shows the
        // "add" affordance until the next fetch. Non-transient still reports.
        if (!isTransientError(error)) {
            ErrorHandler(error instanceof Error ? error : Error(error?.message || 'Exclusion lookup failed'), { query: 'auto-schedule-exclusions', operation: 'fetch' }, 'Something went wrong loading suggestion rules');
        }
        return [];
    }
    return data || [];
};

/**
 * Exclude (person × task × event) from future auto-schedule suggestions.
 * Server is idempotent on the unique triple (returns the stored row).
 * @returns Promise<{exclusionID, organizationID, personID, taskID, eventID, createdByPersonID, createdAt}>
 */
export const addAutoScheduleExclusion = async ({ taskID, eventID, personID }) => {
    const { data, error } = await supabase.rpc('add_auto_schedule_exclusion', {
        p_task_id: taskID,
        p_event_id: eventID,
        p_person_id: personID,
    });
    if (error) throwExclusionError(error, 'add_auto_schedule_exclusion');
    return data;
};

/** Remove a previously stored "do not suggest" rule by its exclusion id. */
export const removeAutoScheduleExclusion = async ({ exclusionID }) => {
    const { data, error } = await supabase.rpc('remove_auto_schedule_exclusion', {
        p_exclusion_id: exclusionID,
    });
    if (error) throwExclusionError(error, 'remove_auto_schedule_exclusion');
    return data;
};
